import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, Mail, CheckCircle, Heart } from 'lucide-react';
import { authApi } from '../lib/api';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'sent'>('idle');

  const handleSubmit = async () => {
    if (!email.trim()) return;
    setStatus('loading');
    try {
      await authApi.forgotPassword(email.trim());
    } catch {
      // Always show success to prevent email enumeration
    }
    setStatus('sent');
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#f8f5f5] flex flex-col max-w-md mx-auto shadow-2xl overflow-hidden">
      <header className="p-6 flex items-center relative z-10">
        <button
          onClick={() => {
            navigate('/');
            setTimeout(
              () =>
                window.dispatchEvent(
                  new CustomEvent('open-login', { detail: { source: 'welcome' } }),
                ),
              50,
            );
          }}
          className="size-10 rounded-full hover:bg-slate-200/50 flex items-center justify-center transition-colors"
        >
          <ArrowLeft size={24} className="text-slate-600" />
        </button>
      </header>

      <div className="flex-1 flex flex-col px-8 relative z-10">
        <div className="mb-8">
          <div className="size-16 rounded-[2rem] bg-[#fa8585] flex items-center justify-center text-white shadow-xl shadow-[#fa8585]/30 mb-4">
            <Heart size={32} fill="white" />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {status !== 'sent' ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-4"
            >
              <div className="mb-6">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2">
                  Reset your password
                </h1>
                <p className="text-slate-500 text-sm">
                  Enter your email and we'll send you a reset link
                </p>
              </div>

              <div className="space-y-3">
                <div className="relative">
                  <Mail
                    size={20}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                    placeholder="Email address"
                    autoFocus
                    className="w-full bg-white border border-slate-200 rounded-2xl py-3 pl-12 pr-4 text-slate-900 font-medium placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all shadow-sm"
                  />
                </div>
                <button
                  onClick={handleSubmit}
                  disabled={status === 'loading' || !email.trim()}
                  className="w-full bg-slate-900 text-white font-bold py-4 rounded-2xl shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:scale-100 flex items-center justify-center gap-2 mt-2"
                >
                  {status === 'loading' ? (
                    <>
                      <svg
                        className="animate-spin size-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      Sending…
                    </>
                  ) : (
                    'Send reset link'
                  )}
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="sent"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="flex flex-col items-center text-center pt-4">
                <div className="size-20 rounded-full bg-emerald-50 flex items-center justify-center mb-6">
                  <CheckCircle size={40} className="text-emerald-500" />
                </div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-3">
                  Check your email
                </h1>
                <p className="text-slate-500 text-sm leading-relaxed">
                  We sent a reset link to <span className="font-bold text-slate-700">{email}</span>.
                  Check your inbox.
                </p>
              </div>

              <button
                onClick={() => {
                  navigate('/');
                  setTimeout(
                    () =>
                      window.dispatchEvent(
                        new CustomEvent('open-login', { detail: { source: 'welcome' } }),
                      ),
                    50,
                  );
                }}
                className="w-full bg-slate-900 text-white font-bold py-4 rounded-2xl shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2"
              >
                <ArrowLeft size={18} /> Back to login
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
